import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const FOOTER_LOGO = '/images/TinyWinsLogo.jpeg';

const FooterModern: React.FC = () => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [stripeScriptLoaded, setStripeScriptLoaded] = useState(false);

  useEffect(() => {
    if (showPaymentModal && !stripeScriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/buy-button.js';
      script.async = true;
      script.onload = () => {
        setStripeScriptLoaded(true);
      };
      document.body.appendChild(script);
    }
  }, [showPaymentModal, stripeScriptLoaded]);

  return (
    <footer className="bg-[#181e29] text-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          <div className="col-span-2 md:col-span-1 flex flex-col">
            <Link
              to="/"
              className="inline-flex mb-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded"
              aria-label="Home"
            >
              <img src={FOOTER_LOGO} alt="" className="h-8 w-auto object-contain rounded" width={120} height={32} />
            </Link>
            <p className="text-gray-400 text-sm leading-snug mb-3 max-w-[200px]">
              Educational developmental support for neurodivergent children and the people who care for them.
            </p>
            <a href="mailto:info@tinywins.ai" className="flex items-center text-gray-400 hover:text-white text-sm transition-colors">
              <Mail className="h-4 w-4 mr-1.5 shrink-0" />
              info@tinywins.ai
            </a>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Product</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/parents" className="hover:text-white transition-colors">Parents</Link></li>
              <li><Link to="/therapists" className="hover:text-white transition-colors">Therapists</Link></li>
              <li><Link to="/schools-and-counties" className="hover:text-white transition-colors">Schools &amp; Counties</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link></li>
              <li><Link to="/developmental-milestone-tracker" className="hover:text-white transition-colors">Milestone tracker</Link></li>
              <li><Link to="/iep-goal-coach" className="hover:text-white transition-colors">IEP Goal Coach</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Resources</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/resources" className="hover:text-white transition-colors">Resource hub</Link></li>
              <li><Link to="/resources/iep" className="hover:text-white transition-colors">IEP</Link></li>
              <li><Link to="/resources/communication" className="hover:text-white transition-colors">Communication</Link></li>
              <li><Link to="/resources/sensory" className="hover:text-white transition-colors">Sensory</Link></li>
              <li><Link to="/resources/behavior-and-regulation" className="hover:text-white transition-colors">Behavior &amp; regulation</Link></li>
              <li><Link to="/resources/visual-schedules" className="hover:text-white transition-colors">Visual schedules</Link></li>
              <li><Link to="/social-stories" className="hover:text-white transition-colors">Social stories</Link></li>
              <li><Link to="/sensory-strategies" className="hover:text-white transition-colors">Sensory strategies</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Company</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              <li><Link to="/team" className="hover:text-white transition-colors">Team</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              <li><Link to="/support" className="hover:text-white transition-colors">Support</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
              <li><Link to="/" className="hover:text-white transition-colors">Sign In</Link></li>
              <li><Link to="/delete-my-account" className="hover:text-white transition-colors">Delete my account</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-6 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">© {new Date().getFullYear()} TinyWins.ai. All rights reserved.</p>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-400 shrink-0" aria-hidden />
            <span className="text-xs text-gray-500">Privacy-minded by design</span>
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPaymentModal(false)}
        >
          <div className="bg-white rounded-lg p-8 max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment</h2>
            <div className="flex justify-center">
              {stripeScriptLoaded ? (
                React.createElement('stripe-buy-button', {
                  'buy-button-id': 'buy_btn_1SOeKyJuyqavHZDhvbtZJOCf',
                  'publishable-key':
                    'pk_test_51SCNrPJuyqavHZDhFKS9SqyyfNLs6YtEC3EndXtWqUVPW2O0505b39r4O2etu5VpKM1NPDKhKEE4LU1whIGmIlpB00JMeArFHr',
                })
              ) : (
                <div className="text-gray-500">Loading payment form...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default FooterModern;
